// =======================================================
// Meldung an das Ticketsystem („Feedback" in der Kopfzeile)
//
// Wer merkt, dass auf einer Seite etwas fehlt oder hakt, schreibt es auf —
// den Rest sammeln wir hier: genauer Link, Seite und Schritt, wer meldet,
// Browser, Bildschirm, Version und die letzten Fehler aus dem Browser. Dazu
// auf Wunsch ein Foto der Seite.
//
// Randori Mail und Ticketsystem liegen im selben Supabase-Projekt. Das
// Ticket entsteht deshalb direkt über submit_app_ticket (Bereich „IT &
// Systeme", mit dem Werkzeug „Randori Mail" verknüpft); Anhänge gehen über
// dieselbe geprüfte Upload-Funktion wie im öffentlichen Ticket-Portal.
// =======================================================
import { supabase } from './supabase';
import { NAV_KATALOG } from './nav';
import { fehlerprotokoll } from './fehlerprotokoll';

/** Kennung von Randori Mail im Portfolio (PortfolioApp_apps.slug). */
const APP_SLUG = 'mailkampagnen';
const APP_NAME = 'Randori Mail';

export const MAX_ANHAENGE = 5;
export const MAX_DATEI_MB = 10;
export const ERLAUBTE_TYPEN = 'image/png,image/jpeg,image/gif,image/webp,image/heic,image/heif,application/pdf';

export interface SeitenKontext {
  link: string;
  seite: string;
  bereich: string | null;
  kampagnenId: string | null;
  zeitpunkt: Date;
  browser: string;
  fenster: string;
  dunkel: boolean;
}

export interface Melder {
  name: string;
  email: string;
  rolle: string;
}

const kurz = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1).trimEnd()}…` : s);
const sauber = (s: string | null | undefined) => (s ?? '').replace(/\s+/g, ' ').trim();

/**
 * Liest, wo die Person gerade steht. Die Überschrift der Seite samt
 * Unterzeile (im Assistenten z. B. „Geburtstagsmail · Wilmersdorf") kommt
 * direkt aus der Ansicht — so passt es auf jeder Seite, ohne dass jede
 * Seite etwas melden muss.
 */
export function seitenKontextLesen(): SeitenKontext {
  const h1 = document.querySelector<HTMLElement>('.main-content h1');
  const titel = sauber(h1?.textContent);
  const unter = h1?.nextElementSibling;
  const unterText = unter?.tagName === 'P' ? sauber(unter.textContent) : '';
  const seite = [titel, unterText && unterText.length <= 100 ? unterText : '']
    .filter(Boolean)
    .join(' · ') || document.title || location.pathname;

  // Längster passender Menüpunkt („/" passt sonst immer).
  const pfad = location.pathname;
  const treffer = NAV_KATALOG
    .filter((it) => (it.end ? pfad === it.path : pfad === it.path || pfad.startsWith(`${it.path}/`)))
    .sort((a, b) => b.path.length - a.path.length)[0];

  // Kampagne im Editor oder Mail im Assistenten: die ID steht in der Adresse.
  const kampagnenId = pfad.match(/\/(?:campaigns|neue-mail)\/([0-9a-f]{8}-[0-9a-f-]{27})/i)?.[1] ?? null;

  return {
    link: location.href,
    seite,
    bereich: treffer?.label ?? null,
    kampagnenId,
    zeitpunkt: new Date(),
    browser: navigator.userAgent,
    fenster:
      `Fenster ${window.innerWidth} × ${window.innerHeight}, ` +
      `Bildschirm ${screen.width} × ${screen.height}, ` +
      `Pixeldichte ${Math.round((window.devicePixelRatio || 1) * 100) / 100}`,
    dunkel: document.documentElement.classList.contains('dark'),
  };
}

const zeitFormat = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit',
});
const uhrFormat = new Intl.DateTimeFormat('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

/** Der Block, der unter jede Meldung kommt — so steht er auch im Ticket. */
export function technischeAngaben(
  k: SeitenKontext,
  m: Melder,
  mitScreenshot: boolean,
  /** Weitere Seiten, von denen ein Foto anhängt (Reihenfolge wie aufgenommen). */
  weitere: SeitenKontext[] = [],
): string {
  const version = `${__APP_VERSION__}, Stand ${zeitFormat.format(new Date(__BUILD_TIME__))}`;
  const melder = [
    m.name || m.email,
    m.name && m.email ? `(${m.email})` : '',
    m.rolle ? `· ${m.rolle}` : '',
  ].filter(Boolean).join(' ');

  const zeilen = [
    '### Technische Angaben',
    `- **Werkzeug:** ${APP_NAME} (Version ${version})`,
    `- **Seite:** ${k.seite}`,
    k.bereich && `- **Menüpunkt:** ${k.bereich}`,
    `- **Link:** ${k.link}`,
    k.kampagnenId && `- **Kampagnen-ID:** \`${k.kampagnenId}\``,
    `- **Gemeldet von:** ${melder}`,
    `- **Zeitpunkt:** ${zeitFormat.format(k.zeitpunkt)}`,
    `- **Browser:** \`${k.browser.replace(/`/g, "'")}\``,
    `- **Anzeige:** ${k.fenster}, ${k.dunkel ? 'dunkles' : 'helles'} Erscheinungsbild`,
    `- **Screenshot:** ${mitScreenshot ? 'automatisch aufgenommen, hängt an' : 'keiner automatisch'}`,
    weitere.length > 0 && '- **Weitere Seiten (Foto hängt an):**',
    ...weitere.map((w, i) => (
      `  ${i + 1}. ${w.seite} — ${w.link} — ${uhrFormat.format(w.zeitpunkt)}` +
      (w.kampagnenId ? ` — Kampagnen-ID \`${w.kampagnenId}\`` : '')
    )),
    '',
    '### Fehlerprotokoll (letzte Meldungen aus dem Browser)',
  ].filter((z): z is string => typeof z === 'string');

  const protokoll = fehlerprotokoll();
  if (protokoll.length === 0) {
    zeilen.push('Keine Fehler oder Warnungen seit dem Laden der Seite.');
  } else {
    for (const e of protokoll.slice(-10)) {
      zeilen.push(`- ${uhrFormat.format(e.zeit)} ${e.art}: \`${e.text.replace(/`/g, "'")}\``);
    }
  }
  return zeilen.join('\n');
}

/**
 * Foto des sichtbaren Ausschnitts. Elemente mit data-melden-ignorieren (der
 * Meldedialog selbst) bleiben draußen. Gibt null zurück, wenn es nicht klappt
 * — die Meldung geht dann eben ohne Bild.
 */
export async function screenshotAufnehmen(dateiname?: string): Promise<File | null> {
  try {
    const { domToBlob } = await import('modern-screenshot');
    const hintergrund = getComputedStyle(document.body).backgroundColor;
    const blob = await domToBlob(document.body, {
      width: window.innerWidth,
      height: window.innerHeight,
      scale: Math.min(window.devicePixelRatio || 1, 2),
      backgroundColor: hintergrund,
      type: 'image/png',
      style: { margin: '0', overflow: 'hidden' },
      // Nur der sichtbare Ausschnitt: Der Inhalt rückt um die Scrollweite
      // nach oben, der Rest wird abgeschnitten — so zeigt das Bild, was die
      // Person beim Klicken vor Augen hatte.
      onCloneNode: (klon) => {
        const inhalt = (klon as HTMLElement).firstElementChild as HTMLElement | null;
        if (!inhalt) return;
        inhalt.style.marginTop = `${-window.scrollY}px`;
        inhalt.style.marginLeft = `${-window.scrollX}px`;
      },
      filter: (el) => !(el instanceof HTMLElement && el.dataset.meldenIgnorieren !== undefined),
      timeout: 8000,
    });
    if (!blob || blob.size === 0) return null;
    const stempel = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    return new File([blob], dateiname ?? `screenshot-${stempel}.png`, { type: 'image/png' });
  } catch (e) {
    console.warn('Screenshot für die Meldung nicht möglich:', e);
    return null;
  }
}

/** Dateiname für ein Foto einer weiteren Seite: Seite und Uhrzeit. */
export function dateinameFuer(k: SeitenKontext): string {
  const seite = (k.bereich ?? k.seite)
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
    .slice(0, 30).replace(/-+$/, '') || 'seite';
  const uhr = [k.zeitpunkt.getHours(), k.zeitpunkt.getMinutes(), k.zeitpunkt.getSeconds()]
    .map((n) => String(n).padStart(2, '0')).join('-');
  return `screenshot-${seite}-${uhr}.png`;
}

export function dateienPruefen(
  vorhanden: File[],
  neu: File[],
  max = MAX_ANHAENGE,
): { dateien: File[]; fehler: string } {
  const erlaubt = ERLAUBTE_TYPEN.split(',');
  const dateien = [...vorhanden];
  let fehler = '';
  for (const f of neu) {
    if (dateien.length >= max) {
      fehler = `Höchstens ${MAX_ANHAENGE} Anhänge inklusive Foto der Seite.`;
      break;
    }
    if (f.type && !erlaubt.includes(f.type)) {
      fehler = `„${f.name}" geht nicht — erlaubt sind Bilder und PDF.`;
      continue;
    }
    if (f.size > MAX_DATEI_MB * 1024 * 1024) {
      fehler = `„${f.name}" ist größer als ${MAX_DATEI_MB} MB.`;
      continue;
    }
    dateien.push(f);
  }
  return { dateien, fehler };
}

export interface MeldungErgebnis {
  nummer: string;
  meineTicketsUrl: string | null;
  anhaengeFehlgeschlagen: number;
}

export async function meldungAbschicken(opts: {
  beschreibung: string;
  kontext: SeitenKontext;
  melder: Melder;
  anhaenge: File[];
  mitScreenshot: boolean;
  /** Weitere Seiten, von denen ein Foto anhängt. */
  weitere?: SeitenKontext[];
}): Promise<MeldungErgebnis> {
  if (!supabase) throw new Error('Keine Verbindung zum Ticketsystem.');
  const { beschreibung, kontext, melder, anhaenge, mitScreenshot, weitere = [] } = opts;

  const ersteZeile = sauber(beschreibung.split('\n').find((z) => z.trim()) ?? '');
  const titel = kurz(`${APP_NAME} · ${kurz(kontext.seite, 70)}: ${ersteZeile}`, 200);
  const text = `${beschreibung.trim()}\n\n${technischeAngaben(kontext, melder, mitScreenshot, weitere)}`;

  const { data, error } = await supabase
    .rpc('submit_app_ticket', {
      p_app_slug: APP_SLUG,
      p_title: titel,
      p_description: text,
      p_creator_name: melder.name || null,
    })
    .single();
  if (error) throw new Error(error.message || 'Das Ticket ließ sich nicht anlegen.');

  const t = data as {
    ticket_id: string;
    ticket_nummer: string;
    zugangs_token: string | null;
    bereich_id: string | null;
    ticketsystem_url: string | null;
  };

  let anhaengeFehlgeschlagen = 0;
  if (t.zugangs_token) {
    for (const datei of anhaenge) {
      const fd = new FormData();
      fd.append('token', t.zugangs_token);
      fd.append('ticket_id', t.ticket_id);
      fd.append('file', datei);
      if (melder.name) fd.append('uploader_name', melder.name);
      const { data: up, error: upErr } = await supabase.functions.invoke('upload-attachment-by-token', { body: fd });
      if (upErr || (up as { error?: string } | null)?.error) {
        anhaengeFehlgeschlagen++;
        console.warn('Anhang ließ sich nicht hochladen:', (up as { error?: string } | null)?.error ?? upErr?.message);
      }
    }
  } else {
    anhaengeFehlgeschlagen = anhaenge.length;
  }

  // Bestätigung an die meldende Person und Hinweis ans Team. Ein Fehler hier
  // soll die Meldung nicht kippen — das Ticket steht ja schon.
  supabase.functions
    .invoke('notify-new-ticket', {
      body: {
        ticket_id: t.ticket_id,
        ticket_number: t.ticket_nummer,
        title: titel,
        creator_name: melder.name || melder.email,
        creator_email: melder.email,
        queue_id: t.bereich_id,
        access_token: t.zugangs_token,
      },
    })
    .catch((e) => console.warn('Benachrichtigung zur Meldung fehlgeschlagen:', e));

  const basis = (t.ticketsystem_url ?? '').replace(/\/+$/, '');
  return {
    nummer: t.ticket_nummer,
    meineTicketsUrl: basis ? `${basis}/meine-tickets` : null,
    anhaengeFehlgeschlagen,
  };
}
